import React, { useState, useRef, ReactNode } from 'react';
import { TouchableWithoutFeedback, TouchableOpacity, StyleSheet, Platform, ViewStyle, GestureResponderEvent } from 'react-native';
import { useHover } from 'react-native-web-hooks';
import { checkColor } from '@styles/colors';

// Define the type for the hitSlop prop
type HitSlop = { top: number; bottom: number; left: number; right: number } | number;

// Define the types for the props
interface TouchableElementProps {
  href?: string;
  pressStyle?: boolean;
  useOpacity?: boolean;
  activeOpacity?: number;
  hitSlop?: HitSlop;
  disabled?: boolean;
  testID?: string;
  onPress?: (event: GestureResponderEvent) => void;
  onPressIn?: (event: GestureResponderEvent) => void;
  onPressOut?: (event: GestureResponderEvent) => void;
  style?: ViewStyle;
  children: (props: {
    pressColor: (color: string) => string;
    hoverColor: (color: string) => string;
    pressHoverColor: (color: string) => string;
    boxStyle: ViewStyle;
    hovered: boolean;
    pressed: boolean;
  }) => ReactNode;
}

const TouchableElement: React.FC<TouchableElementProps> = ({
  href,
  pressStyle = true,
  useOpacity,
  activeOpacity,
  hitSlop = { top: 15, bottom: 15, left: 15, right: 15 },
  disabled,
  onPress,
  onPressIn,
  onPressOut,
  style,
  children,
  testID,
}) => {
  const ref = useRef(null);
  const [pressed, setPressed] = useState(false);
  const hovered = useHover(ref);
  const Component = useOpacity ? TouchableOpacity : TouchableWithoutFeedback;

  const pressColor = (c: string) => (pressed ? checkColor(c, 'pressed') : c);
  const hoverColor = (c: string) => (hovered ? checkColor(c, 'hover') : c);
  const pressHoverColor = (c: string) => {
    if (!pressStyle) return c;
    if (pressed) return checkColor(c, 'pressed');
    if (hovered) return checkColor(c, 'hover');
    return c;
  };

  const elHitSlop = typeof hitSlop === 'number' ? { top: hitSlop, bottom: hitSlop, left: hitSlop, right: hitSlop } : hitSlop;

  return (
    <Component
      testID={testID}
      activeOpacity={activeOpacity}
      focusable={false}
      disabled={disabled}
      onPress={onPress}
      style={[style]}
      ref={ref}
      hitSlop={elHitSlop}
      onPressIn={(e) => {
        if (onPressIn) {
          onPressIn(e);
        }
        setPressed(true);
      }}
      onPressOut={(e) => {
        if (onPressOut) {
          onPressOut(e);
        }
        setPressed(false);
      }}
      href={href}
    >
      {children({
        pressColor,
        hoverColor,
        pressHoverColor,
        boxStyle: styles.box,
        hovered,
        pressed,
      })}
    </Component>
  );
};

const styles = StyleSheet.create({
  box: {
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
      default: {},
    }),
  } as any,
});

export default TouchableElement;
